// Owns safe tool activity and its typed result, decision, and artifact slots.
// Raw tool names and arguments never become rendered copy. The event feed
// separately validates a query's bounded description for completed traces.
import { Fragment, type ReactNode } from 'react';
import { z } from 'zod';

import { startOutreachForShifts } from '@apps/web/features/phoebe_chat_v3/api/outreach_api';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { selectionActionMessage } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import {
    approvalEditorSchema,
    approvalPresentationSchema,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_approval_required';
import {
    collapsedApprovalTitle,
    ControlledPermissionView,
    NoApprovalCommunicationView,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call_approval_views';
import { ControlledQuestionView } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call_question_views';
import {
    type ApprovalFragment,
    type CompoundSlotDeclaration,
    defineCompoundOwner,
    type GeneratedTablePresentation,
    type QuestionFragment,
    type SafeRenderContext,
    SELECTABLE_ID_COLUMNS,
    type SelectableIdColumn,
    type ToolCompoundState,
    type ToolResultFragment,
    type ToolResultPresentation,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { AgencyRulesPreviewResource } from '@apps/web/features/phoebe_chat_v3/ui/agency_rules_preview';
import { V3ChatAttachmentCard } from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_card';
import { GeneratedTableResource } from '@apps/web/features/phoebe_chat_v3/ui/generated_table';
import { LiveOutreachCard } from '@apps/web/features/phoebe_chat_v3/ui/live_outreach_card';
import { ActionResultCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/action_result_card';
import { ApprovalStackSlot } from '@apps/web/features/phoebe_chat_v3/ui/primitives/approval_stack';
import { CompactWriteCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_write_card';
import { type GeneratedTableSelection } from '@apps/web/features/phoebe_chat_v3/ui/primitives/generated_table';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { SettingsReceiptCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/settings_receipt_card';
import { RecommendationsCard } from '@apps/web/features/phoebe_chat_v3/ui/recommendations_card';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { toast } from '@apps/web/lib/app_notifications';

const optionalStringSchema = z.string().nullable().default(null);
const nonblankStringSchema = z
    .string()
    .refine((value) => value.trim().length > 0);
const choiceSchema = z
    .object({
        label: nonblankStringSchema,
        description: optionalStringSchema,
    })
    .strict();
const actionSchema = z
    .object({
        value: optionalStringSchema,
        label: optionalStringSchema,
        style: z.enum(['primary', 'secondary', 'ghost']),
    })
    .strict();
const itemSchema = z
    .object({
        id: z.string(),
        title: z.string(),
        detail: optionalStringSchema,
        tone: z
            .enum(['neutral', 'success', 'warning', 'danger'])
            .nullable()
            .default(null),
    })
    .strict();
const presentationCommon = {
    title: optionalStringSchema,
    description: optionalStringSchema,
};
const presentationWithActions = {
    ...presentationCommon,
    actions: z.array(actionSchema).min(2).max(4),
};
const questionPresentationSchema = z.discriminatedUnion('type', [
    z
        .object({
            type: z.literal('actions'),
            ...presentationWithActions,
        })
        .strict(),
    z
        .object({
            type: z.literal('list'),
            ...presentationCommon,
            items: z.array(itemSchema).min(2).max(12),
            initial_visible_count: z.number().int().nullable().default(null),
        })
        .strict(),
    z
        .object({
            type: z.literal('message_preview'),
            ...presentationWithActions,
            message: optionalStringSchema,
            recipient_count: z.number().int().nullable().default(null),
            recipient_noun: optionalStringSchema,
            recipients: z.array(z.string()).nullable().default(null),
        })
        .strict(),
]);
const safeQuestionDisplaySchema = z
    .object({
        question: nonblankStringSchema,
        header: z.string().trim().min(1).max(12).nullable().default(null),
        input_type: z.enum(['text', 'single_choice', 'multi_choice', 'yes_no']),
        choices: z.array(choiceSchema).nullable().default(null),
        placeholder: optionalStringSchema,
        allow_other: z.boolean().default(true),
        presentation: questionPresentationSchema.nullable().default(null),
    })
    .strict()
    .refine((display) => {
        const labels = display.choices?.map((choice) => choice.label) ?? [];
        const isChoiceQuestion =
            display.input_type === 'single_choice' ||
            display.input_type === 'multi_choice';
        if (isChoiceQuestion && labels.length < 2) {
            return false;
        }
        if (display.presentation === null) {
            return true;
        }
        if (display.presentation.type === 'list') {
            return (
                isChoiceQuestion &&
                labels.length <= 12 &&
                display.presentation.items.length === labels.length &&
                display.presentation.items.every(
                    (item, index) => item.id === labels[index],
                )
            );
        }
        const primaryActionCount = display.presentation.actions.filter(
            (action) => action.style === 'primary',
        ).length;
        return (
            display.input_type === 'single_choice' &&
            labels.length <= 4 &&
            display.presentation.actions.length === labels.length &&
            display.presentation.actions.every(
                (action, index) =>
                    action.value === null || action.value === labels[index],
            ) &&
            primaryActionCount === 1
        );
    });

const automationTriggerDisplaySchema = z
    .object({
        type: z.enum(['schedule', 'event']),
        schedule: z
            .object({
                kind: z.enum(['daily', 'weekly']),
                time: z.string(),
                weekday: z.number().int().min(0).max(6).nullable().optional(),
            })
            .strict()
            .nullable()
            .optional(),
        event_name: z.string().nullable().optional(),
        label: z.string().nullable().optional(),
        event_filter: z.record(z.string(), z.string()).nullable().optional(),
    })
    .strict();

const automationDisplaySchema = z
    .object({
        resolved_automation_name: z.string().nullable().optional(),
        resolved_trigger: automationTriggerDisplaySchema.nullable().optional(),
    })
    .strict();

export const toolCallPayloadSchema = z
    .object({
        call_id: z.string().min(1),
        approval_reason: z.string().min(1).max(200).optional(),
        // The raw value of each choice, in order, from the feed: the exact
        // string the agent wrote, even when the display label is an id
        // blanked to a placeholder.
        choice_values: z.array(z.string()).nullable().optional(),
        query_description: z
            .string()
            .min(1)
            .max(120)
            .refine((value) => value.trim().length > 0)
            .optional(),
        approval_group: z
            .object({
                call_ids: z.array(z.string().min(1)).min(1).max(100),
            })
            .strip()
            .optional(),
        display_arguments_json: safeQuestionDisplaySchema.nullable().optional(),
        automation_display_json: automationDisplaySchema.nullable().optional(),
        customer_narration_present: z
            .string()
            .min(1)
            .max(120)
            .refine((value) => value.trim().length > 0)
            .optional(),
        communication_presentation: approvalPresentationSchema
            .nullable()
            .optional(),
        communication_editor: approvalEditorSchema.nullable().optional(),
    })
    .strip()
    .transform(({ choice_values: rawChoices, ...safe }) => {
        const displayChoiceCount = safe.display_arguments_json?.choices?.length;
        return {
            ...safe,
            choiceValues:
                rawChoices !== null &&
                rawChoices !== undefined &&
                displayChoiceCount !== undefined &&
                rawChoices.length === displayChoiceCount
                    ? rawChoices
                    : null,
        };
    });

// The failure narration always wins: a description of what the query
// looked for must not read as success when the call failed.
function completedToolLine(
    payload: z.output<typeof toolCallPayloadSchema>,
    result: ToolResultFragment | null,
): string | null {
    if (result === null) {
        return null;
    }
    if (
        result.kind === 'tool_succeeded' &&
        payload.query_description !== undefined
    ) {
        return payload.query_description;
    }
    return result.pastNarration;
}

const initialToolCompoundState: ToolCompoundState = {
    result: null,
    approval: null,
    question: null,
    artifacts: [],
    appliedMemberOccurrenceIds: new Set(),
};

const toolResultSlot: CompoundSlotDeclaration<
    ToolCompoundState,
    'tool_result'
> = {
    name: 'tool_result',
    cardinality: 'one',
    reduce(state, fragment, memberOccurrenceId) {
        if (state.appliedMemberOccurrenceIds.has(memberOccurrenceId)) {
            return state;
        }
        return {
            ...state,
            result: fragment,
            appliedMemberOccurrenceIds: new Set([
                ...state.appliedMemberOccurrenceIds,
                memberOccurrenceId,
            ]),
        };
    },
};

const approvalSlot: CompoundSlotDeclaration<ToolCompoundState, 'approval'> = {
    name: 'approval',
    cardinality: 'one',
    reduce(state, fragment, memberOccurrenceId) {
        if (state.appliedMemberOccurrenceIds.has(memberOccurrenceId)) {
            return state;
        }
        const approval =
            fragment.kind === 'approval_resolved' &&
            state.approval?.kind === 'approval_required'
                ? {
                      ...fragment,
                      presentation: state.approval.presentation,
                      ...(state.approval.editor === null ||
                      state.approval.editor === undefined
                          ? {}
                          : { editor: state.approval.editor }),
                  }
                : fragment;
        return {
            ...state,
            approval,
            appliedMemberOccurrenceIds: new Set([
                ...state.appliedMemberOccurrenceIds,
                memberOccurrenceId,
            ]),
        };
    },
};

const questionSlot: CompoundSlotDeclaration<ToolCompoundState, 'question'> = {
    name: 'question',
    cardinality: 'one',
    reduce(state, fragment, memberOccurrenceId) {
        if (state.appliedMemberOccurrenceIds.has(memberOccurrenceId)) {
            return state;
        }
        return {
            ...state,
            question: fragment,
            appliedMemberOccurrenceIds: new Set([
                ...state.appliedMemberOccurrenceIds,
                memberOccurrenceId,
            ]),
        };
    },
};

const artifactsSlot: CompoundSlotDeclaration<ToolCompoundState, 'artifacts'> = {
    name: 'artifacts',
    cardinality: 'many',
    reduce(state, fragment, memberOccurrenceId) {
        if (state.appliedMemberOccurrenceIds.has(memberOccurrenceId)) {
            return state;
        }
        return {
            ...state,
            artifacts: [...state.artifacts, fragment],
            appliedMemberOccurrenceIds: new Set([
                ...state.appliedMemberOccurrenceIds,
                memberOccurrenceId,
            ]),
        };
    },
};

export type ToolCallPayload = z.output<typeof toolCallPayloadSchema>;
export type SafeQuestionPresentation = NonNullable<
    ToolCallPayload['display_arguments_json']
>;
export type QuestionOption = Readonly<{
    readonly description: string | null;
    readonly label: string;
    readonly value: string;
}>;
export type QuestionStatus = 'waiting' | 'answered' | 'cancelled' | 'expired';
export type PermissionStatus = 'waiting' | 'granted' | 'denied' | 'expired';

// Each result kind declares whether its content owns, augments, or feeds
// the approval surface. Keep precedence in one exhaustive policy.
const RESULT_APPROVAL_POLICY: Record<
    ToolResultPresentation['kind'],
    'replace' | 'alongside' | 'receipt'
> = {
    outreach: 'replace',
    write_receipt: 'receipt',
    table: 'alongside',
    recommendations: 'alongside',
    agency_rules: 'alongside',
    next_steps: 'alongside',
    'chat-attachment': 'alongside',
};

// A text or call that ran without an approval card gets a receipt, whatever
// mode or policy let it skip the card.
function noApprovalCommunicationEditor(
    payload: ToolCallPayload,
    compoundState: ToolCompoundState,
) {
    const editor = payload.communication_editor;
    return compoundState.approval === null &&
        payload.communication_presentation !== null &&
        payload.communication_presentation !== undefined &&
        (editor?.kind === 'sms' || editor?.kind === 'voice')
        ? editor
        : null;
}

function renderApproval(
    fragment: ApprovalFragment | null,
    result: ToolResultFragment | null,
    payload: ToolCallPayload,
    ownerOccurrenceId: string,
    context: SafeRenderContext,
): ReactNode {
    // Permission is granted before the tool runs; the receipt must not
    // claim the send happened until the result says so.
    const outcome =
        result === null
            ? 'pending'
            : result.kind === 'tool_failed'
              ? 'failed'
              : 'succeeded';
    // Both states render through the same stack slot so the height morph
    // from pending card to resolved receipt animates instead of snapping.
    switch (fragment?.kind) {
        case 'approval_required': {
            const uniqueBatchCallIds =
                payload.approval_group === undefined
                    ? null
                    : Array.from(
                          new Set(payload.approval_group.call_ids),
                      ).sort();
            return (
                <ApprovalStackSlot
                    ownerId={ownerOccurrenceId}
                    title={collapsedApprovalTitle(
                        fragment.editor ?? null,
                        fragment.presentation,
                        context.formatPersonName,
                    )}
                >
                    <ControlledPermissionView
                        batchCallIds={uniqueBatchCallIds}
                        callId={payload.call_id}
                        presentation={fragment.presentation}
                        editor={fragment.editor ?? null}
                        requestDecision={context.requestToolDecision}
                        scope={context.scope}
                        status="waiting"
                        approvalReason={payload.approval_reason}
                    />
                </ApprovalStackSlot>
            );
        }
        case 'approval_resolved':
            return (
                <ApprovalStackSlot
                    ownerId={ownerOccurrenceId}
                    title={collapsedApprovalTitle(
                        fragment.editor ?? null,
                        fragment.presentation ?? null,
                        context.formatPersonName,
                    )}
                >
                    <ControlledPermissionView
                        batchCallIds={null}
                        callId={payload.call_id}
                        presentation={fragment.presentation ?? null}
                        editor={fragment.editor ?? null}
                        requestDecision={context.requestToolDecision}
                        scope={context.scope}
                        status={fragment.decision}
                        approvalReason={payload.approval_reason}
                        outcome={outcome}
                        assignmentResult={
                            result?.kind === 'tool_succeeded'
                                ? result.assignmentResult
                                : undefined
                        }
                        communicationOutcome={
                            result?.kind === 'tool_succeeded'
                                ? result.communicationOutcome
                                : undefined
                        }
                        noteResult={
                            result?.kind === 'tool_succeeded'
                                ? result.noteResult
                                : undefined
                        }
                        receipt={
                            result?.presentation?.kind === 'write_receipt'
                                ? result.presentation
                                : undefined
                        }
                    />
                </ApprovalStackSlot>
            );
        case undefined:
            return null;
        default: {
            const exhaustiveFragment: never = fragment;
            return exhaustiveFragment;
        }
    }
}

function renderQuestion(
    fragment: QuestionFragment | null,
    result: ToolResultFragment | null,
    payload: ToolCallPayload,
    context: SafeRenderContext,
): ReactNode {
    // The tool result carries the answer as the values the card knows, so
    // its labels name the receipt; the echo text only stands in before it.
    const resultAnswer =
        result?.kind === 'tool_succeeded' ? result.questionAnswer : undefined;
    switch (fragment?.kind) {
        case 'question_required':
            return (
                <ControlledQuestionView
                    callId={payload.call_id}
                    choiceValues={payload.choiceValues}
                    presentation={payload.display_arguments_json ?? null}
                    requestSubmit={context.requestQuestionSubmit}
                    resolvedAnswer={null}
                    status="waiting"
                />
            );
        case 'question_resolved':
            return (
                <ControlledQuestionView
                    callId={payload.call_id}
                    choiceValues={payload.choiceValues}
                    presentation={payload.display_arguments_json ?? null}
                    requestSubmit={context.requestQuestionSubmit}
                    resolvedAnswer={resultAnswer ?? fragment.answer}
                    status={fragment.resolution}
                />
            );
        case undefined:
            return null;
        default: {
            const exhaustiveFragment: never = fragment;
            return exhaustiveFragment;
        }
    }
}

// render_dynamic_ui already shows the table or card, and suggest_next_steps
// shows its buttons. Their fixed narration must never appear as a second
// line above that result. Assignment completion is already shown by its card.
const SILENT_RENDER_NARRATIONS = new Set([
    'Preparing the results',
    'Prepared the results',
    'Suggesting next steps',
    'Suggested next steps',
]);

function isSilentRenderNarration(text: string): boolean {
    return SILENT_RENDER_NARRATIONS.has(text);
}

function silentRenderNarration(text: string): string {
    return isSilentRenderNarration(text) ? '' : text;
}

// Shift tables carry the product's own Find caregivers, named as it is
// across the app: it creates the outreach at once and moves the reader to
// it, so any model action that would find caregivers or start an outreach
// is dropped in its favour. Other model actions send their prompt. One
// outreach holds at most 21 shifts (fewer when clients differ; the server
// says so). Above that the button waits rather than failing.
const OUTREACH_ACTION = /outreach|callout|caregiver/iu;
const START_OUTREACH_ACTION = {
    label: 'Find caregivers',
    prompt: '',
    maxRows: 21,
    maxRowsMessage: 'Up to 21 shifts per outreach',
} as const;

function tableSelection(
    presentation: GeneratedTablePresentation,
    context: SafeRenderContext,
): GeneratedTableSelection | undefined {
    const selection = presentation.selection;
    if (selection === undefined) {
        return undefined;
    }
    const startsOutreach = selection.idColumn === 'shift_id';
    // Details-viewing actions are steered off at the source: rows open
    // their record, and the render_dynamic_ui description tells the model
    // so. Classifying action intent here by pattern proved unreliable in
    // both directions, so a details action the model still adds renders
    // and works rather than being guessed away.
    const modelActions = startsOutreach
        ? selection.actions.filter(
              (action) =>
                  !OUTREACH_ACTION.test(action.label) &&
                  !OUTREACH_ACTION.test(action.prompt),
          )
        : selection.actions;
    const actions = startsOutreach
        ? [START_OUTREACH_ACTION, ...modelActions]
        : modelActions;
    return {
        idColumn: selection.idColumn,
        actions,
        onAction: (action, ids, completeAction) => {
            if (action.label !== START_OUTREACH_ACTION.label) {
                context.requestSendMessage(
                    selectionActionMessage(
                        action.prompt,
                        selection.idColumn,
                        ids,
                    ),
                );
                return;
            }
            return startOutreachForShifts({
                organizationId: context.scope.organizationId,
                mode: context.scope.mode,
                runId: context.scope.runId,
                sourceEventId: presentation.sourceEventId,
                shiftIds: ids,
            }).then(
                (href) => {
                    completeAction({
                        status: 'Outreach created',
                        label: 'View outreach',
                        href,
                    });
                    context.requestNavigate(href);
                },
                (error: unknown) => {
                    toast.error(
                        error instanceof Error
                            ? error.message
                            : 'Could not start the outreach.',
                        {
                            persistNotification: false,
                        },
                    );
                },
            );
        },
    };
}

// The backend's _context_entity (middleware/dynamic_ui.py) treats a table
// carrying several entity id columns as having no single context entity, so
// a row opens its record only when the row names exactly one record type.
// Checkbox selection keeps its first-match column: its actions carry the
// picked ids as typed references, which stay unambiguous either way.
function rowOpenIdColumn(
    presentation: GeneratedTablePresentation,
): SelectableIdColumn | undefined {
    const idColumns = SELECTABLE_ID_COLUMNS.filter((column) =>
        presentation.columns.includes(column),
    );
    return idColumns.length === 1 ? idColumns[0] : undefined;
}

function renderPresentation(
    presentation: ToolResultPresentation | undefined,
    context: SafeRenderContext,
): ReactNode {
    if (presentation === undefined) {
        return null;
    }
    switch (presentation.kind) {
        case 'outreach':
            return (
                <LiveOutreachCard
                    key={`${context.scope.organizationId}:${context.scope.mode}:${presentation.outreachId}`}
                    scope={context.scope}
                    outreachId={presentation.outreachId}
                    heading={presentation.heading}
                />
            );
        case 'recommendations':
            return (
                <div className="mt-2 min-w-0">
                    <RecommendationsCard
                        key={`${context.scope.organizationId}:${context.scope.mode}:${presentation.outreachId}`}
                        scope={context.scope}
                        outreachId={presentation.outreachId}
                        organizationTimeZone={context.organizationTimeZone}
                    />
                </div>
            );
        case 'write_receipt': {
            if (presentation.shiftCard !== undefined) {
                return (
                    <CompactWriteCard
                        card={presentation.shiftCard}
                        state={{ status: presentation.status ?? 'succeeded' }}
                        notices={presentation.notices}
                    />
                );
            }
            if (presentation.settingsChanges !== undefined) {
                return <SettingsReceiptCard receipt={presentation} />;
            }
            return (
                <ActionResultCard
                    status={presentation.status ?? 'succeeded'}
                    title={presentation.title}
                    summary={presentation.summary}
                    notices={presentation.notices}
                />
            );
        }
        case 'chat-attachment':
            return (
                <div className="min-w-0">
                    <V3ChatAttachmentCard
                        attachment={{
                            ...presentation,
                            runId: context.scope.runId,
                        }}
                        context={{
                            organizationId: context.scope.organizationId,
                            organizationTimezone: context.organizationTimeZone,
                            mode: context.scope.mode,
                        }}
                    />
                </div>
            );
        case 'table':
            return (
                <div className="mt-2 min-w-0">
                    <GeneratedTableResource
                        scope={context.scope}
                        tableOutreaches={context.tableOutreaches}
                        title={presentation.title}
                        workspacePath={presentation.workspacePath}
                        format={presentation.format}
                        rowCount={presentation.rowCount}
                        columns={presentation.columns}
                        columnsTruncated={presentation.columnsTruncated}
                        sourceEventId={presentation.sourceEventId}
                        uiContextReceipt={presentation.uiContextReceipt}
                        selection={tableSelection(presentation, context)}
                        rowIdColumn={rowOpenIdColumn(presentation)}
                    />
                </div>
            );
        case 'next_steps':
            // The buttons are an answer-level affordance: the harness offers
            // the newest one under the answer this presentation sits behind.
            return null;
        case 'agency_rules':
            return (
                <div className="mt-2 min-w-0">
                    <AgencyRulesPreviewResource
                        scope={context.scope}
                        workspacePath={presentation.workspacePath}
                        rowCount={presentation.rowCount}
                        sourceEventId={presentation.sourceEventId}
                    />
                </div>
            );
        default: {
            const exhaustivePresentation: never = presentation;
            return exhaustivePresentation;
        }
    }
}

export type ToolOutputPresentationHarnessProps = Readonly<{
    readonly presentation: ToolResultPresentation;
    readonly scope: HarnessScope;
}>;

export function ToolOutputPresentationHarness({
    presentation,
    scope,
}: ToolOutputPresentationHarnessProps): ReactNode {
    const { formatName } = useFormatPersonName();
    const context: SafeRenderContext = {
        scope,
        nowMs: 0,
        organizationTimeZone: scope.organizationTimeZone,
        formatPersonName: formatName,
        isExpanded: true,
        tableOutreaches: [],
        requestGroupToggle: () => {},
        requestRetry: () => {},
        requestQuestionSubmit: async () => 'ignored',
        requestToolDecision: async () => 'ignored',
        requestSendMessage: () => {},
        requestNavigate: () => {},
    };
    return renderPresentation(presentation, context);
}

export const toolCallDefinitions = {
    tool_call: defineCompoundOwner('tool_call', {
        disposition: 'compound_owner',
        schema: toolCallPayloadSchema,
        malformed: {
            kind: 'placeholder',
            customerCopy: 'Working on the request',
        },
        evidence: null,
        keys(payload, occurrence) {
            if (occurrence.committed === null) {
                return [{ kind: 'call_id', value: payload.call_id }];
            }
            // Event identity wins before aliases so members never attach by
            // a weaker key when the exact owner relationship is available.
            if (occurrence.committed.command_id === null) {
                return [
                    {
                        kind: 'event_id',
                        value: occurrence.committed.id,
                    },
                    { kind: 'call_id', value: payload.call_id },
                ];
            }
            return [
                { kind: 'event_id', value: occurrence.committed.id },
                {
                    kind: 'command_id',
                    value: occurrence.committed.command_id,
                },
                { kind: 'call_id', value: payload.call_id },
            ];
        },
        initialState: initialToolCompoundState,
        slots: [toolResultSlot, approvalSlot, questionSlot, artifactsSlot],
        issues(payload) {
            return payload.customer_narration_present === undefined
                ? [{ reason: 'missing_customer_narration' }]
                : [];
        },
        group: {
            kind: 'activity',
            boundary: 'consecutive_same_kind',
            carriesCard(payload, compoundState) {
                return (
                    noApprovalCommunicationEditor(payload, compoundState) !==
                    null
                );
            },
            phase(_payload, compoundState) {
                if (compoundState.result === null) {
                    return 'active';
                }
                return compoundState.result.kind === 'tool_failed'
                    ? 'failed'
                    : 'completed';
            },
            presentSummary(payload) {
                return silentRenderNarration(
                    payload.customer_narration_present ??
                        'Working on the request',
                );
            },
            pastSummary(payload, compoundState) {
                if (
                    compoundState.approval !== null &&
                    compoundState.result?.kind === 'tool_succeeded' &&
                    compoundState.result.assignmentResult?.status ===
                        'assigned' &&
                    (compoundState.result.assignmentResult.notices?.length ??
                        0) === 0 &&
                    compoundState.result.assignmentResult.shifts.length > 0 &&
                    compoundState.result.assignmentResult.shifts.every(
                        (shift) => shift.assigned,
                    )
                ) {
                    return '';
                }
                return silentRenderNarration(
                    completedToolLine(payload, compoundState.result) ??
                        'Completed the request',
                );
            },
        },
        render(payload, compoundState, occurrence, context) {
            if (compoundState.question !== null) {
                return renderQuestion(
                    compoundState.question,
                    compoundState.result,
                    payload,
                    context,
                );
            }
            const narration =
                completedToolLine(payload, compoundState.result) ??
                payload.customer_narration_present ??
                'Working on the request';
            const receiptEditor = noApprovalCommunicationEditor(
                payload,
                compoundState,
            );
            const noApprovalCommunication =
                receiptEditor === null ? null : (
                    <NoApprovalCommunicationView
                        approvalReason={payload.approval_reason}
                        editor={receiptEditor}
                        executionOutcome={
                            compoundState.result === null
                                ? 'pending'
                                : compoundState.result.kind === 'tool_failed'
                                  ? 'failed'
                                  : 'succeeded'
                        }
                        communicationOutcome={
                            compoundState.result?.kind === 'tool_succeeded'
                                ? compoundState.result.communicationOutcome
                                : undefined
                        }
                    />
                );
            // A card is the row's whole content: a result presentation
            // (table, agency rules, outreach receipt) or an approval card
            // already carries what happened, so fixed narration never draws
            // as loose text beside one. The petal row and the activity
            // group's summaries still narrate the step while it runs.
            const presentation = compoundState.result?.presentation;
            const approvalPolicy =
                presentation === undefined
                    ? 'alongside'
                    : RESULT_APPROVAL_POLICY[presentation.kind];
            const hideNarration =
                isSilentRenderNarration(narration) ||
                compoundState.result?.presentation !== undefined ||
                compoundState.approval !== null ||
                noApprovalCommunication !== null;
            return (
                <Fragment>
                    {hideNarration ? null : (
                        <span>
                            <ResolvedReferenceText text={narration} />
                        </span>
                    )}
                    {renderPresentation(
                        compoundState.approval !== null &&
                            approvalPolicy === 'receipt'
                            ? undefined
                            : presentation,
                        context,
                    )}
                    {renderApproval(
                        approvalPolicy === 'replace'
                            ? null
                            : compoundState.approval,
                        compoundState.result,
                        payload,
                        occurrence.occurrenceId,
                        context,
                    )}
                    {noApprovalCommunication}
                </Fragment>
            );
        },
    }),
} as const;

export const toolCallContractFixtures = {
    valid: {
        call_id: 'call-1',
        display_arguments_json: {
            question: 'Which option should Phoebe use?',
            header: null,
            input_type: 'single_choice',
            choices: [
                { label: 'Option A', description: null },
                { label: 'Option B', description: null },
            ],
            placeholder: null,
            allow_other: true,
            presentation: null,
        },
        customer_narration_present: 'Checking agency records',
    },
    malformed: { call_id: 7 },
} as const;
